import type { ReactNode } from "react"
import { createFileRoute } from "@tanstack/react-router"

import { pageHead } from "@/lib/metadata"
import { getPricing } from "@/server/web"
import { ArrowUpRight } from "lucide-react"

import { UnavailableAlert } from "@/components/feedback/unavailable-alert"
import { Container } from "@/components/layout/container"
import { SiteShell } from "@/components/layout/site-shell"
import { FreePlanCta } from "@/components/pricing/free-plan-cta"
import { IntervalSwitch } from "@/components/pricing/interval-switch"
import {
  FeatureList,
  PlanFrame,
  PlanHead,
} from "@/components/pricing/plan-card"
import { Badge } from "@anysola/ui/badge"
import { Button } from "@anysola/ui/button"
import type {
  BillingInterval,
  PricingCatalogEntry,
  PricingPlan,
  PricingProduct,
} from "@anysola/contracts/web/pricing"
import { CHECKOUT_URL } from "@/lib/app-links"
import {
  formatTrial,
  PLAN_LABELS,
  yearlySavingsPercent,
  type BillingPlan,
} from "@anysola/contracts/account/billing-core"
import { formatCredits } from "@anysola/contracts/account/credits-core"
import { cn } from "@anysola/ui/utils"

// Static and revalidated, not rendered per visit: plans, prices and
// allowances come from Anysola Account's public pricing endpoint (which reads
// Polar), and none of that belongs on a public page's request path. This TTL
// bounds how long a change there takes to show here.
/** "$20", not "$20.00"; a price with cents keeps them. */
function formatPrice(minorUnits: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
  }).format(minorUnits / 100)
}

/** "/ month", "/ 3 months". */
function per(product: PricingProduct) {
  return product.intervalCount > 1
    ? `/ ${product.intervalCount} ${product.interval}s`
    : `/ ${product.interval}`
}

/**
 * A product description as Polar holds it: plain lines, where lines starting
 * with "- " or "* " are features. Anything else is a sentence, shown small
 * under the list.
 */
function parseDescription(text: string | null) {
  const lines = (text ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
  return {
    features: lines
      .filter((line) => /^[-*]\s+/.test(line))
      .map((line) => line.replace(/^[-*]\s+/, "")),
    prose: lines.filter((line) => !/^[-*]\s+/.test(line)),
  }
}

/** What a free account already includes, so its card isn't an empty box. */
const FREE_FEATURES = ["Support tickets", "Launch updates and betas"]

type Allowances = Record<BillingPlan, number> | null

function PlanCard({
  entry,
  interval,
  allowances,
}: {
  entry: PricingPlan
  interval: BillingInterval
  allowances: Allowances
}) {
  const monthly = entry.products.find(
    (product) => product.interval === "month" && product.intervalCount === 1
  )
  const yearly = entry.products.find(
    (product) => product.interval === "year" && product.intervalCount === 1
  )
  const product =
    entry.products.find((candidate) => candidate.interval === interval) ??
    entry.products[0]
  if (!product) return null

  const savings =
    product.interval === "year" && monthly && yearly
      ? yearlySavingsPercent(monthly, yearly)
      : null
  const trial = formatTrial(product.trialInterval, product.trialIntervalCount)
  const featured = entry.plan === "pro"
  const team = entry.plan === "team"
  const allowance = allowances?.[entry.plan]
  const { features, prose } = parseDescription(product.description)

  return (
    <PlanFrame className={cn("md:p-8", featured && "md:pt-10")}>
      <PlanHead
        featured={featured}
        name={PLAN_LABELS[entry.plan]}
        badge={
          trial && entry.onSale ? (
            <Badge variant="secondary">{trial}</Badge>
          ) : featured ? (
            <span className="text-xs font-medium text-foreground">
              Recommended
            </span>
          ) : null
        }
        price={formatPrice(product.amount, product.currency)}
        period={per(product)}
        note={
          product.interval === "year" && product.intervalCount === 1
            ? `${formatPrice(Math.round(product.amount / 12), product.currency)} / month, billed yearly${savings ? ` · save ${savings}%` : ""}`
            : null
        }
      />
      {entry.onSale ? (
        <Button
          className="mt-5 w-full md:mt-8"
          size="lg"
          variant={featured ? "default" : "secondary"}
          render={
            <a
              href={`${CHECKOUT_URL}/?plan=${entry.plan}&interval=${product.interval}`}
            />
          }
        >
          {trial ? "Start free trial" : `Choose ${PLAN_LABELS[entry.plan]}`}
          <ArrowUpRight />
        </Button>
      ) : (
        <Button
          className="mt-5 w-full md:mt-8"
          size="lg"
          variant="secondary"
          disabled
        >
          Coming soon
        </Button>
      )}
      <p className="mt-8 text-sm text-muted-foreground">
        Everything in {team ? PLAN_LABELS.pro : PLAN_LABELS.free}, and
      </p>
      {allowance ? (
        <div className="mt-3">
          <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-4xl leading-none font-light tracking-[-0.035em] text-foreground tabular-nums">
              {formatCredits(allowance)}
            </span>
            <span className="text-muted-foreground">
              credits / mo{team ? " / seat" : ""}
            </span>
          </p>
          <div aria-hidden className="mt-5 h-1.5 rounded-full bg-foreground" />
        </div>
      ) : null}
      <FeatureList
        items={[
          ...(team && entry.summary ? [entry.summary] : []),
          ...(allowance && !team ? ["Credits work in every Anysola app"] : []),
          ...features,
        ]}
      />
      {prose.map((line) => (
        <p key={line} className="mt-4 text-xs text-muted-foreground">
          {line}
        </p>
      ))}
    </PlanFrame>
  )
}

/**
 * One flat-priced product from Account's catalog: its price on the chosen
 * interval (or the only one it has) and a checkout link. No credits, no
 * plan: the product is the whole deal.
 */
function ProductCard({
  entry,
  interval,
}: {
  entry: PricingCatalogEntry
  interval: BillingInterval
}) {
  const product =
    entry.products.find((candidate) => candidate.interval === interval) ??
    entry.products[0]
  if (!product) return null
  const trial = formatTrial(product.trialInterval, product.trialIntervalCount)
  const { features, prose } = parseDescription(product.description)

  return (
    <PlanFrame>
      <PlanHead
        name={entry.name}
        badge={
          trial && entry.onSale ? (
            <Badge variant="secondary">{trial}</Badge>
          ) : null
        }
        price={formatPrice(product.amount, product.currency)}
        period={per(product)}
        note={
          product.interval === "year" && product.intervalCount === 1
            ? `${formatPrice(Math.round(product.amount / 12), product.currency)} / month, billed yearly`
            : null
        }
      />
      {entry.onSale ? (
        <Button
          className="mt-5 w-full"
          size="lg"
          variant="secondary"
          render={
            <a
              href={`${CHECKOUT_URL}/product?product=${encodeURIComponent(entry.slug)}&interval=${product.interval}`}
            />
          }
        >
          {trial ? "Start free trial" : "Subscribe"}
          <ArrowUpRight />
        </Button>
      ) : (
        <Button className="mt-5 w-full" size="lg" variant="secondary" disabled>
          Coming soon
        </Button>
      )}
      {features.length > 0 && <FeatureList items={features} />}
      {prose.map((line) => (
        <p key={line} className="mt-4 text-xs text-muted-foreground">
          {line}
        </p>
      ))}
    </PlanFrame>
  )
}

/** The catalog products under the plans, as cards; nothing when there are none. */
function ProductGrid({
  catalog,
  interval,
}: {
  catalog: PricingCatalogEntry[]
  interval: BillingInterval
}) {
  const entries = catalog.filter((entry) => entry.products.length > 0)
  if (entries.length === 0) return null
  return (
    <section className="mt-12 sm:mt-16">
      <h2 className="mb-4 text-sm font-medium text-muted-foreground sm:mb-6">
        Products
      </h2>
      <div className="grid gap-4 md:grid-cols-3 md:gap-6">
        {entries.map((entry) => (
          <ProductCard key={entry.slug} entry={entry} interval={interval} />
        ))}
      </div>
    </section>
  )
}

function PlanGrid({
  plans,
  interval,
  allowances,
  missing,
}: {
  plans: PricingPlan[]
  interval: BillingInterval
  allowances: Allowances
  /** Without paid plans: why, in the column beside Free, so the grid
   *  stays whole. */
  missing?: ReactNode
}) {
  const free = allowances?.free
  return (
    <div
      className={cn(
        "grid gap-4 md:gap-6",
        // Pro sits in the middle, wider: the plan most visitors want.
        plans.length >= 2 ? "md:grid-cols-[1fr_1.2fr_1fr]" : "md:grid-cols-2"
      )}
    >
      <PlanFrame className="md:p-8">
        <PlanHead
          name={PLAN_LABELS.free}
          price={formatPrice(0, plans[0]?.products[0]?.currency ?? "usd")}
          period="forever"
        />
        <FreePlanCta />
        <FeatureList
          items={[
            ...(free ? [`${formatCredits(free)} credits every month`] : []),
            ...FREE_FEATURES,
          ]}
        />
      </PlanFrame>
      {plans.map((entry) => (
        <PlanCard
          key={entry.plan}
          entry={entry}
          interval={interval}
          allowances={allowances}
        />
      ))}
      {plans.length === 0 && missing}
    </div>
  )
}

function PricingPage() {
  // A failure here must not fail the build or a revalidation (see
  // `readPricing`). `plans: null` means Polar isn't configured on Account.
  const pricing = Route.useLoaderData()
  const pricingUnavailable = pricing === "unavailable"
  const plans = pricingUnavailable
    ? []
    : (pricing.plans ?? []).filter((entry) => entry.products.length > 0)
  const allowances = pricingUnavailable ? null : pricing.allowances
  const catalog = pricingUnavailable ? [] : (pricing.catalog ?? [])
  const hasYearly = plans.some((entry) =>
    entry.products.some((product) => product.interval === "year")
  )
  const hasMonthly = plans.some((entry) =>
    entry.products.some((product) => product.interval === "month")
  )
  const bestSaving = Math.max(
    0,
    ...plans.map((entry) => {
      const monthly = entry.products.find(
        (product) => product.interval === "month" && product.intervalCount === 1
      )
      const yearly = entry.products.find(
        (product) => product.interval === "year" && product.intervalCount === 1
      )
      return (monthly && yearly && yearlySavingsPercent(monthly, yearly)) || 0
    })
  )

  return (
    <SiteShell>
      <h1 className="sr-only">Pricing</h1>

      <Container className="pt-12 pb-16 sm:pt-16 sm:pb-24">
        {hasYearly && hasMonthly ? (
          <IntervalSwitch
            monthly={
              <>
                <PlanGrid
                  plans={plans}
                  interval="month"
                  allowances={allowances}
                />
                <ProductGrid catalog={catalog} interval="month" />
              </>
            }
            yearly={
              <>
                <PlanGrid
                  plans={plans}
                  interval="year"
                  allowances={allowances}
                />
                <ProductGrid catalog={catalog} interval="year" />
              </>
            }
            yearlyNote={bestSaving > 0 ? `−${bestSaving}%` : null}
          />
        ) : (
          <>
            <PlanGrid
              plans={plans}
              interval={hasYearly ? "year" : "month"}
              allowances={allowances}
              missing={
                pricingUnavailable ? (
                  <UnavailableAlert
                    className="self-start"
                    title="Paid plans couldn’t be loaded right now."
                  />
                ) : (
                  <p className="self-center px-1 text-sm text-muted-foreground">
                    Paid plans aren’t available yet.
                  </p>
                )
              }
            />
            <ProductGrid
              catalog={catalog}
              interval={hasYearly ? "year" : "month"}
            />
          </>
        )}

        <p className="mt-10 text-center text-xs text-muted-foreground">
          Tax is added at checkout where it applies.
        </p>
      </Container>
    </SiteShell>
  )
}

export const Route = createFileRoute("/pricing")({
  // `?billing=yearly` opens the yearly grid (`IntervalSwitch`).
  validateSearch: (
    search: Record<string, unknown>
  ): { billing?: "yearly" } => ({
    billing: search.billing === "yearly" ? "yearly" : undefined,
  }),
  // Plans, prices and allowances come from Anysola Account's public
  // pricing endpoint (which reads Polar); the service keeps the last good copy
  // and re-reads it every 300 s, the Next page's `revalidate`.
  loader: () => getPricing(),
  head: () =>
    pageHead({
      title: "Pricing",
      description:
        "Anysola plans and monthly credits: start free, one credit balance for every app.",
      path: "/pricing",
      image: "/pricing/opengraph-image",
    }),
  component: PricingPage,
})
